import { useState } from 'react';

const contactFormUrl = 'https://readdy.ai/api/form/d9pkdfti2vhsdh2d36a0';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const hpField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (hpField && hpField.value.trim() !== '') {
      setFormState('success');
      form.reset();
      setTimeout(() => setFormState('idle'), 3000);
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (hpField) formData.delete('website_alt');

      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
        setTimeout(() => setFormState('idle'), 4000);
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.meta as Record<string, string>)?.detail || responseText || '提交失败，请稍后重试';
        setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91heilia.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于91黑料',
              description: '了解91黑料的品牌故事、平台口碑与联系方式',
              url: 'https://91heilia.com/about',
              isPartOf: { '@type': 'WebSite', name: '91黑料', url: 'https://91heilia.com/' },
            },
          ]),
        }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
            关于91黑料
          </h1>
          <p className="mt-3 text-foreground-600 max-w-2xl mx-auto">
            了解我们的品牌故事、平台理念与核心团队
          </p>
        </div>

        {/* Brand Introduction */}
        <section className="mb-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-100 mb-4">
                <a href="#!">品牌故事</a>
              </h2>
              <div className="space-y-4 text-foreground-500 leading-relaxed text-sm">
                <p>
                  <strong>91黑料</strong>成立于2020年，是国内领先的爆料资讯聚合平台。我们致力于为公众提供真实、及时、深度的内幕资讯，让每一个普通人都能了解事件背后的真相。我们的slogan "<strong>黑料不打烊</strong>"不仅是一个口号，更是我们对用户每一天的承诺。
                </p>
                <p>
                  平台汇聚了来自各行各业的爆料人和深度调查记者，通过严格的审核机制，确保每一条资讯的真实性和可信度。内容涵盖娱乐爆料、社会内幕、科技黑料、财经揭秘、网红八卦、政商秘闻等多个领域。
                </p>
                <p>
                  我们相信，信息的透明是社会进步的基石。<strong>91黑料最新</strong>动态保持每日更新，让用户第一时间获取最有价值的爆料资讯。无论是最新91黑料动态，还是深度内幕调查，我们都力求做到最快、最准、最全。
                </p>
              </div>
            </div>
            <div className="relative w-full h-[350px] rounded-2xl overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20dark%20newsroom%20with%20dramatic%20lighting%2C%20journalism%20theme%2C%20professional%20atmosphere%2C%20dark%20charcoal%20interior%20with%20warm%20accent%20lights%2C%20cinematic%20photography%2C%20editorial%20style&width=800&height=700&seq=about-brand&orientation=landscape"
                alt="91黑料品牌形象"
                title="91黑料 - 品牌故事"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="mb-14">
          <h2 className="font-heading text-2xl font-bold text-foreground-100 text-center mb-8">
            <a href="#!">平台口碑</a>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '5000万+', label: '月活跃用户' },
              { value: '10万+', label: '累计爆料文章' },
              { value: '99.7%', label: '内容真实率' },
              { value: '2000+', label: '合作爆料人' },
            ].map((stat, i) => (
              <div key={i} className="bg-background-100 border border-background-300/40 rounded-xl p-6 text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</div>
                <div className="text-xs text-foreground-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-14">
          <h2 className="font-heading text-2xl font-bold text-foreground-100 text-center mb-8">
            <a href="#!">用户评价</a>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { name: '匿名用户A', text: '"91黑料的爆料速度真的快，很多热点事件我都是在这里第一时间看到的。内容质量也很高，不是那种标题党。"', role: '资深读者' },
              { name: '匿名用户B', text: '"作为一个媒体从业者，我很认可91黑料的审核机制。每条爆料都有来源标注，这在当今的信息环境中非常难得。"', role: '媒体从业者' },
              { name: '匿名用户C', text: '"之前通过91黑料了解到一个投资骗局，成功避免了损失。感谢平台的深度调查，希望你们越做越好。"', role: '忠实用户' },
            ].map((review, i) => (
              <div key={i} className="bg-background-100 border border-background-300/40 rounded-xl p-5">
                <div className="flex items-center gap-1.5 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-500 leading-relaxed mb-3">{review.text}</p>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary-500/10 flex items-center justify-center text-primary-500 text-xs font-bold">
                    {review.name.charAt(review.name.length - 1)}
                  </span>
                  <div>
                    <div className="text-xs font-medium text-foreground-300">{review.name}</div>
                    <div className="text-[11px] text-foreground-700">{review.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="mb-14" id="contact">
          <h2 className="font-heading text-2xl font-bold text-foreground-100 text-center mb-8">
            <a href="#!">联系我们</a>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold text-foreground-200 mb-5">联系方式</h3>
              <div className="space-y-4">
                {[
                  { icon: 'ri-mail-line', label: '爆料邮箱', value: 'tips@91heilia.com' },
                  { icon: 'ri-mail-send-line', label: '商务合作', value: 'business@91heilia.com' },
                  { icon: 'ri-phone-line', label: '客服热线', value: '400-888-9191' },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区光华路9号SOHO现代城A座1208室' },
                  { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00 - 18:00' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 border border-background-300/40 shrink-0">
                      <i className={`${item.icon} text-foreground-500`}></i>
                    </span>
                    <div>
                      <div className="text-xs text-foreground-700 mb-0.5">{item.label}</div>
                      <div className="text-sm text-foreground-300 font-medium">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h3 className="text-lg font-semibold text-foreground-200 mb-5">在线留言</h3>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4 bg-background-100 border border-background-300/40 rounded-xl p-5"
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  className="hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-500 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-200 placeholder-foreground-700 focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-500 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-200 placeholder-foreground-700 focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-500 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-200 placeholder-foreground-700 focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-500 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-200 placeholder-foreground-700 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="w-full py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                >
                  {formState === 'submitting' ? '提交中...' : '提交留言'}
                </button>

                {formState === 'success' && (
                  <p className="text-sm text-green-400 text-center">留言已成功提交，我们会尽快回复您！</p>
                )}
                {formState === 'error' && formError && (
                  <p className="text-sm text-red-400 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}